import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useOutletContext, useParams } from "react-router-dom";
import style from "./ProfileDetails.module.css";
import { baseurl, appUrl } from "../Url";
import Loader from "../Loder/Loader";
import { MdArrowBack, MdEmail, MdPhone, MdLocationOn, MdLink, MdSchool, MdWork, MdCode, MdDescription, MdPerson, MdInsertDriveFile, MdShare, MdMoreVert } from "react-icons/md";
import CalendarView from "./CalendarView";

const ProfileDetails = () => {
  const { onMenuToggle } = useOutletContext() || {};
  const navigate = useNavigate();
  const location = useLocation();
  const { userId: routeUserId } = useParams();
  const currentUserId = localStorage.getItem("USERID");
  const profileUserId = location.state?.userID || routeUserId || currentUserId;
  const USERID = profileUserId;
  const OrgID = localStorage.getItem("ORGID");
  const isViewingOtherProfile = !!location.state?.userID && location.state.userID !== currentUserId;
  const hideShareLink = location.state?.fromCommunity === true;
  const showCalendar = false;
  const [loading, setLoading] = useState(true);
  const [userHasNoDetails, setUserHasNoDetails] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);

  // Profile data states
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [personalInfo, setPersonalInfo] = useState({
    fullName: "",
    email: "",
    phone: "",
    phoneCountryCode: "+91",
    city: "",
    country: "",
  });
  const [summary, setSummary] = useState("");
  const [education, setEducation] = useState([]);
  const [workExperience, setWorkExperience] = useState([]);
  const [projects, setProjects] = useState([]);
  const [skills, setSkills] = useState([]);

  const getResumeViewUrl = (fileUrl, fileName = "") => {
    const normalizedName = String(fileName || "").toLowerCase();
    const normalizedUrl = String(fileUrl || "").toLowerCase();
    const isOfficeDoc =
      normalizedName.endsWith(".doc") ||
      normalizedName.endsWith(".docx") ||
      normalizedUrl.includes(".doc") ||
      normalizedUrl.includes(".docx");

    if (isOfficeDoc) {
      return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(fileUrl)}`;
    }
    return `${baseurl}/resume/view?url=${encodeURIComponent(fileUrl)}&filename=${encodeURIComponent(fileName || "resume")}`;
  };
  const [links, setLinks] = useState([]);
  const [resume, setResume] = useState({
    file_name: "",
    upload_date: "",
    file_url: "",
  });

  const resolveProfileImageSrc = (urlValue) => {
    const normalizedUrl = String(urlValue || "").trim();
    if (!normalizedUrl) return "";
    if (normalizedUrl.startsWith("data:")) return normalizedUrl;
    return `${normalizedUrl}${normalizedUrl.includes("?") ? "&" : "?"}uid=${USERID || ""}`;
  };

  useEffect(() => {
    if (USERID) {
      fetchProfileDetails();
    } else {
      setLoading(false);
    }
  }, [USERID]);

  // Capture referral when user lands via shared profile link (?ref=...)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const rawCode = params.get("ref") || params.get("ref_code") || params.get("code");
    if (!rawCode) return;

    const fetchReferral = async () => {
      try {
        const res = await fetch(`${baseurl}/referral/link?code=${encodeURIComponent(rawCode)}`);
        const data = await res.json();
        if (data && data.status && data.data && data.data.referral_code) {
          localStorage.setItem("duno_referral_code", data.data.referral_code);
          if (data.data.referrer_user_id) {
            localStorage.setItem("duno_referral_user_id", data.data.referrer_user_id);
          }
          localStorage.setItem("duno_referral_asset_category", data.data.referral_asset_category || "PROFILE");
          localStorage.setItem("duno_referral_asset_id", data.data.referral_asset_id || USERID);
        }
      } catch (e) {
        console.warn("Failed to capture referral for profile:", e);
      }
    };

    fetchReferral();
  }, [USERID]);

  // Record referral visit once for logged-in visitors (non-referrer)
  useEffect(() => {
    if (typeof window === "undefined" || !currentUserId || !USERID) return;
    const referralCode = localStorage.getItem("duno_referral_code");
    const referrerUserId = localStorage.getItem("duno_referral_user_id");
    const assetCategory = localStorage.getItem("duno_referral_asset_category") || "PROFILE";
    const assetId = localStorage.getItem("duno_referral_asset_id") || USERID;

    if (!referralCode || !referrerUserId || !assetId) return;
    if (String(referrerUserId) === String(currentUserId)) return;

    const dedupeKey = `duno_referral_visit_recorded_${referralCode}_${currentUserId}_${assetId}`;
    if (sessionStorage.getItem(dedupeKey) === "1") return;

    fetch(`${baseurl}/referral/record-action`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        referrer_user_id: referrerUserId,
        referee_id: currentUserId,
        referee_username: localStorage.getItem("username") || localStorage.getItem("email") || "",
        referral_asset_category: assetCategory,
        referral_asset_id: assetId,
        action: "LINK_VISIT",
      }),
    })
      .then(() => {
        sessionStorage.setItem(dedupeKey, "1");
      })
      .catch((err) => {
        console.warn("Failed to record profile referral visit:", err);
      });
  }, [currentUserId, USERID]);

  const fetchProfileDetails = async () => {
    setUserHasNoDetails(false);
    try {
      // Fetch candidate profile data
      const candidateProfileResponse = await fetch(`${baseurl}/candidate/profile/get`, {
        method: "POST",
        body: JSON.stringify({ user_id: USERID }),
        headers: {
          "Content-Type": "application/json",
        },
      });

      const candidateProfileData = await candidateProfileResponse.json();

      const hasProfileData = candidateProfileData.status === true && candidateProfileData.data && candidateProfileData.data.length > 0;
      if (!hasProfileData) {
        setUserHasNoDetails(true);
      }
      if (hasProfileData) {
        const profile = candidateProfileData.data[0];

        // Populate personal info
        if (profile.personal_info) {
          setPersonalInfo({
            fullName: profile.personal_info.full_name || "",
            email: profile.personal_info.email || "",
            phone: profile.personal_info.phone || "",
            phoneCountryCode: profile.personal_info.phone_country_code || "+91",
            city: profile.personal_info.city || "",
            country: profile.personal_info.country || "",
          });
        }

        // Populate resume
        if (profile.resume) {
          setResume({
            file_name: profile.resume.file_name || "",
            upload_date: profile.resume.upload_date || "",
            file_url: profile.resume.file_url || "",
          });
        }

        // Populate summary
        setSummary(profile.summary || "");

        // Populate education
        if (profile.education && Array.isArray(profile.education)) {
          setEducation(profile.education);
        }

        // Populate work experience
        if (profile.work_experience && Array.isArray(profile.work_experience)) {
          setWorkExperience(profile.work_experience);
        }

        // Populate projects
        if (profile.projects && Array.isArray(profile.projects)) {
          setProjects(profile.projects);
        }

        // Populate links
        if (profile.links && Array.isArray(profile.links)) {
          setLinks(profile.links);
        }

        // Populate skills
        if (profile.skills && Array.isArray(profile.skills)) {
          setSkills(profile.skills);
        }

        // Fetch profile image URL and role from user profile endpoint
        try {
          const key = (() => { try { const v = localStorage.getItem("access-token"); return v && v !== "undefined" ? JSON.parse(v) : null; } catch { return null; } })();
          const userProfileResponse = await fetch(`${baseurl}/user/profile`, {
            method: "POST",
            body: JSON.stringify({ user_id: USERID, org_id: OrgID }),
            headers: {
              "Content-Type": "application/json",
              ...(key && { Authorization: `Bearer ${key}` }),
            },
          });
          const userProfileData = await userProfileResponse.json();
          if (userProfileData.status === true && userProfileData.data) {
            const ud = userProfileData.data;
            if (ud.profile_image && ud.profile_image.trim() !== "") {
              setProfileImageUrl(ud.profile_image);
            }
          }
        } catch (error) {
          console.error("Error fetching profile image:", error);
        }
      }
    } catch (error) {
      console.error("Error loading profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    } catch (error) {
      return dateString;
    }
  };

  const shareUrl = USERID ? `${appUrl}/profile/${USERID}` : "";
  const handleShare = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!shareUrl) return;
    let url = shareUrl;
    if (!isViewingOtherProfile && USERID && baseurl) {
      try {
        const token = localStorage.getItem("access-token");
        let bearer = token;
        if (token && typeof token === "string" && token.startsWith("{")) {
          try {
            const parsed = JSON.parse(token);
            bearer = parsed?.token ?? parsed ?? token;
          } catch {
            bearer = token;
          }
        }
        const res = await fetch(`${baseurl}/referral/link`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(bearer && { Authorization: `Bearer ${bearer}` }),
          },
          body: JSON.stringify({
            user_id: USERID,
            asset_category: "PROFILE",
            asset_id: USERID,
          }),
        });
        const data = await res.json();
        if (data.status && data.data?.referral_code) {
          url = `${url}?ref=${data.data.referral_code}`;
        }
      } catch (err) {
        console.warn("Referral link fetch failed:", err);
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setCopySnackbarOpen(true);
      setTimeout(() => {
        setShareCopied(false);
        setCopySnackbarOpen(false);
      }, 2000);
    } catch {
      setShareCopied(true);
      setCopySnackbarOpen(true);
      setTimeout(() => {
        setShareCopied(false);
        setCopySnackbarOpen(false);
      }, 2000);
    }
  };

  const formatSkillName = (skill) => {
    const skillStr = typeof skill === 'string' ? skill : (skill?.title ?? skill?.name ?? skill?.skill_name ?? String(skill ?? ''));
    if (!skillStr || typeof skillStr !== 'string') return '';
    const specialCases = {
      'javascript': 'JavaScript',
      'js': 'JavaScript',
      'python': 'Python',
      'html': 'HTML',
      'css': 'CSS',
      'react': 'React',
      'nodejs': 'Node.js',
      'node.js': 'Node.js',
    };
    const lowerSkill = skillStr.toLowerCase().trim();
    if (specialCases[lowerSkill]) {
      return specialCases[lowerSkill];
    }
    return skillStr
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleBackNavigation = () => {
    if (location.state?.fromCommunity === true) {
      navigate("/community", { state: { openCommunityTab: "Members" } });
      return;
    }
    navigate(isViewingOtherProfile ? "/" : "/profile");
  };

  if (loading) {
    return <Loader />;
  }

  if (userHasNoDetails) {
    return (
      <div className={style.container}>
        <div className={style.profileDetails}>
          <div className={style.header}>
            <button
              type="button"
              className={style.mobileHeaderMenuBtn}
              onClick={onMenuToggle}
              aria-label="Open menu"
            >
              <MdMoreVert size={22} />
            </button>

            <button className={style.backButton} onClick={handleBackNavigation}>
              <MdArrowBack /> {isViewingOtherProfile ? "Back" : "Back to Profile"}
            </button>
            {!hideShareLink && shareUrl && (
              <button
                type="button"
                className={`${style.shareButton} ${shareCopied ? style.shareCopied : ""}`}
                onClick={handleShare}
                title={shareCopied ? "Copied!" : "Share profile"}
              >
                <MdShare size={20} />
                <span>{shareCopied ? "Link copied!" : "Share"}</span>
              </button>
            )}

            {/* user image icon removed for profile-details page */}
          </div>
          <div className={style.noDetailsMessage}>
            <MdPerson className={style.noDetailsIcon} />
            <p>This user has not provided details.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={style.container}>
      <div className={style.profileDetails}>
        {/* Header with Back Button and Share */}
        <div className={style.header}>
          <button
            type="button"
            className={style.mobileHeaderMenuBtn}
            onClick={onMenuToggle}
            aria-label="Open menu"
          >
            <MdMoreVert size={22} />
          </button>

          <button className={style.backButton} onClick={handleBackNavigation}>
            <MdArrowBack /> {isViewingOtherProfile ? "Back" : "Back to Profile"}
          </button>
          {!hideShareLink && shareUrl && (
            <button
              type="button"
              className={`${style.shareButton} ${shareCopied ? style.shareCopied : ""}`}
              onClick={handleShare}
              title={shareCopied ? "Copied!" : "Share profile"}
            >
              <MdShare size={20} />
              <span>{shareCopied ? "Link copied!" : "Share"}</span>
            </button>
          )}

          {/* user image icon removed for profile-details page */}
        </div>

        {/* Two Column Layout */}
        <div className={style.contentWrapper}>
          {/* Resume-like Layout */}
          <div className={`${style.resumeContainer} ${!showCalendar ? style.resumeContainerFull : ""}`}>
          {/* Header Section with Photo and Contact Info */}
          <div className={style.resumeHeader}>
            <div className={style.headerContent}>
              <div className={style.profileImageContainer}>
                {profileImageUrl ? (
                  <img
                    src={resolveProfileImageSrc(profileImageUrl)}
                    alt="Profile"
                    className={style.profileImage}
                  />
                ) : (
                  <div className={style.profileImagePlaceholder}>
                    <MdPerson className={style.profileImagePlaceholderIcon} />
                  </div>
                )}
              </div>
              <div className={style.headerInfo}>
                <h2 className={style.name}>{personalInfo.fullName || "Not provided"}</h2>
                <div className={style.contactInfo}>
                  {personalInfo.email && (
                    <div className={style.contactItem}>
                      <MdEmail className={style.contactIcon} />
                      <span>{personalInfo.email}</span>
                    </div>
                  )}
                  {personalInfo.phone && (
                    <div className={style.contactItem}>
                      <MdPhone className={style.contactIcon} />
                      <span>{personalInfo.phoneCountryCode} {personalInfo.phone}</span>
                    </div>
                  )}
                  {(personalInfo.city || personalInfo.country) && (
                    <div className={style.contactItem}>
                      <MdLocationOn className={style.contactIcon} />
                      <span>
                        {[personalInfo.city, personalInfo.country].filter(Boolean).join(", ")}
                      </span>
                    </div>
                  )}
                  {links
                    .filter((l) => l.url)
                    .map((link, index) => (
                      <div key={`${link.url}-${index}`} className={style.contactItem}>
                        <MdLink className={style.contactIcon} />
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={style.link}
                        >
                          {link.name || link.url}
                        </a>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>

          {/* Summary Section */}
          {summary && (
            <div className={style.section}>
              <h3 className={style.sectionTitle}>
                <MdDescription className={style.sectionIcon} />
                Summary
              </h3>
              <p className={style.sectionContent}>{summary}</p>
            </div>
          )}

          {/* Education Section */}
          {education.length > 0 && (
            <div className={style.section}>
              <h3 className={style.sectionTitle}>
                <MdSchool className={style.sectionIcon} />
                Education
              </h3>
              {education.map((edu, index) => (
                <div key={index} className={style.entry}>
                  <div className={style.entryHeader}>
                    <h4 className={style.entryTitle}>{edu.degree || "Degree"}</h4>
                    {(edu.start_year || edu.end_year) && (
                      <span className={style.entryDate}>
                        {edu.start_year || ""} {edu.end_year ? `- ${edu.end_year}` : ""}
                      </span>
                    )}
                  </div>
                  {edu.school && <p className={style.entrySubtitle}>{edu.school}</p>}
                  <div className={style.entryDetails}>
                    {edu.major && <span className={style.detailItem}>Major: {edu.major}</span>}
                    {edu.gpa && <span className={style.detailItem}>GPA: {edu.gpa}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Work Experience Section */}
          {workExperience.length > 0 && (
            <div className={style.section}>
              <h3 className={style.sectionTitle}>
                <MdWork className={style.sectionIcon} />
                Work Experience
              </h3>
              {workExperience.map((exp, index) => (
                <div key={index} className={style.entry}>
                  <div className={style.entryHeader}>
                    <h4 className={style.entryTitle}>{exp.role || "Role"}</h4>
                    {(exp.start_year || exp.end_year) && (
                      <span className={style.entryDate}>
                        {exp.start_year || ""} {exp.end_year ? `- ${exp.end_year}` : "Present"}
                      </span>
                    )}
                  </div>
                  {exp.company && <p className={style.entrySubtitle}>{exp.company}</p>}
                  {(exp.city || exp.country) && (
                    <p className={style.entryLocation}>
                      {[exp.city, exp.country].filter(Boolean).join(", ")}
                    </p>
                  )}
                  {exp.description && <p className={style.entryDescription}>{exp.description}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Projects Section */}
          {projects.length > 0 && (
            <div className={style.section}>
              <h3 className={style.sectionTitle}>
                <MdCode className={style.sectionIcon} />
                Projects
              </h3>
              {projects.map((proj, index) => (
                <div key={index} className={style.entry}>
                  <div className={style.entryHeader}>
                    <h4 className={style.entryTitle}>{proj.project_name || "Project"}</h4>
                    {(proj.start_year || proj.end_year) && (
                      <span className={style.entryDate}>
                        {proj.start_year || ""} {proj.end_year ? `- ${proj.end_year}` : ""}
                      </span>
                    )}
                  </div>
                  {proj.description && <p className={style.entryDescription}>{proj.description}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Skills Section */}
          {skills.length > 0 && (
            <div className={style.section}>
              <h3 className={style.sectionTitle}>
                <MdCode className={style.sectionIcon} />
                Skills
              </h3>
              <div className={style.skillsContainer}>
                {skills.map((skill, index) => (
                  <span key={index} className={style.skillTag}>
                    {formatSkillName(skill)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Resume Section */}
          {resume.file_url && (
            <div className={style.section}>
              <h3 className={style.sectionTitle}>
                <MdInsertDriveFile className={style.sectionIcon} />
                Resume
              </h3>
              <div className={style.resumeSection}>
                {resume.file_name && (
                  <p className={style.resumeFileName}>
                    <span className={style.resumeFileValue}>{resume.file_name}</span>
                  </p>
                )}
                {resume.upload_date && (
                  <p className={style.resumeDate}>
                    <strong>Uploaded:</strong>
                    <span className={style.resumeDateValue}>{formatDate(resume.upload_date)}</span>
                  </p>
                )}
                <a
                  href={getResumeViewUrl(resume.file_url, resume.file_name || "resume")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={style.resumeLink}
                >
                  View Resume
                </a>
              </div>
            </div>
          )}

          {/* Links Section */}
          {links.length > 0 && (
            <div className={style.section}>
              <h3 className={style.sectionTitle}>
                <MdLink className={style.sectionIcon} />
                Links
              </h3>
              <div className={style.linksContainer}>
                {links.map((link, index) => (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={style.linkItem}
                  >
                    {link.name || link.url}
                  </a>
                ))}
              </div>
            </div>
          )}
          </div>

          {/* Calendar View on Right Side */}
          {showCalendar && (
            <div className={style.calendarSection}>
              <CalendarView userId={USERID} />
            </div>
          )}
        </div>
      </div>
      {copySnackbarOpen && (
        <div className={style.snackbar}>
          <p>Link copied</p>
        </div>
      )}
    </div>
  );
};

export default ProfileDetails;

